import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";
import { formatDuration, formatViews } from "@/lib/movies";
import SectionHeading from "@/components/feature/SectionHeading";

interface HotRankingProps {
  movies: Movie[];
}

export default function HotRanking({ movies }: HotRankingProps) {
  if (!movies.length) return null;
  const top = movies[0];
  const rest = movies.slice(1);

  return (
    <section className="border-y border-background-200/50 bg-background-100/50">
      <div className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-16">
        <SectionHeading
          eyebrow="Most watched"
          title="热门大片推荐"
          description="按累计播放量排序，看看这周大家都在看什么。"
          actionLabel="完整榜单"
          actionTo="/category/全部?sort=hot"
        />

        <div className="grid gap-4 lg:grid-cols-5 lg:gap-5">
          <Link
            to={`/detail/${top.id}`}
            className="group relative flex min-h-[300px] overflow-hidden rounded-lg border border-background-200/70 lg:col-span-2 lg:min-h-[420px]"
          >
            <img
              src={top.backdrop || top.poster}
              alt={`${top.title}剧照`}
              title={`${top.title} 在线观看 - 免费看大片`}
              className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-background-50/5" />
            <div className="relative z-10 flex w-full flex-col justify-end p-5 md:p-6">
              <span className="mb-3 inline-flex w-fit items-center gap-1.5 rounded-md bg-accent-500 px-2.5 py-1 text-xs font-medium text-background-50">
                <i className="ri-fire-fill text-[11px]" />
                播放量 TOP 1
              </span>
              <h3 className="font-heading text-2xl font-semibold text-foreground-50 md:text-3xl">
                {top.title}
              </h3>
              <p className="mt-2 text-xs text-foreground-400 md:text-sm">
                {top.year} · {top.region} · {top.genres.join(" / ")} · {formatDuration(top.duration)}
              </p>
              <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-foreground-300">
                {top.synopsis}
              </p>
              <div className="mt-4 flex items-center gap-4 text-xs text-foreground-400">
                <span className="inline-flex items-center gap-1 text-accent-300">
                  <i className="ri-star-fill text-[11px]" />
                  {top.rating.toFixed(1)} 分
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-play-circle-line text-sm" />
                  {formatViews(top.views)}次播放
                </span>
              </div>
            </div>
          </Link>

          <div className="grid gap-3 sm:grid-cols-2 lg:col-span-3">
            {rest.map((movie, index) => (
              <Link
                key={movie.id}
                to={`/detail/${movie.id}`}
                className="group flex items-center gap-3 rounded-lg border border-background-200/60 bg-background-50/60 p-3 transition-colors hover:border-primary-500/50 hover:bg-background-200/50"
              >
                <span className="w-5 shrink-0 text-center font-heading text-lg font-semibold text-foreground-500 transition-colors group-hover:text-accent-400">
                  {index + 2}
                </span>
                <img
                  src={movie.poster}
                  alt={`${movie.title}海报`}
                  title={`${movie.title} 在线观看 - 免费看大片`}
                  className="h-[86px] w-[60px] shrink-0 rounded-md object-cover object-top"
                />
                <div className="min-w-0 flex-1">
                  <h3 className="line-clamp-1 font-heading text-sm font-medium text-foreground-100 transition-colors group-hover:text-primary-300">
                    {movie.title}
                  </h3>
                  <p className="mt-1 text-xs text-foreground-500">
                    {movie.year} · {movie.region}
                  </p>
                  <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-500">
                    {movie.synopsis}
                  </p>
                  <p className="mt-1.5 flex items-center gap-2 text-xs">
                    <span className="inline-flex items-center gap-0.5 text-accent-400">
                      <i className="ri-star-fill text-[10px]" />
                      {movie.rating.toFixed(1)}
                    </span>
                    <span className="text-foreground-600">{formatViews(movie.views)}播放</span>
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}